import { Page } from './../../shared/models/page';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { Reservation } from '../models/reservation';
import { ReservationService } from './reservation-http-service';
import { ReservationStatusCode } from 'app/event/models/reservation-status-code.enum';
import { RoomStaySummary } from '../models/room-stay-summary';
import { EventStore } from 'app/event/service/event.store';
import { OnyxEvent } from '../models/event';
import { EventReservationSource } from '../models/event-reservation-source';
import { TransientReservationService } from './transient-reservation-http-service';
import { TransactionUtils } from '../utils/transaction-utils';
import { TranslateService } from '@ngx-translate/core';
import { Response } from '@angular/http';
import { AbstractEventHasItemsStore } from './abstract-event-has-items.store';
import { EventHttpService } from './event.http.service';
import { catchError, filter, take, tap } from 'rxjs/operators';
import { EventHasItemsByVendor } from '../models/event-has-items-by-vendor';

@Injectable()
export class ReservationStore extends AbstractEventHasItemsStore {

  private currentReservations: BehaviorSubject<List<Reservation>> = new BehaviorSubject(List([]));

  constructor(
    private flashMessagesService: FlashMessagesService,
    private reservationHttpService: ReservationService,
    private eventStore: EventStore,
    private transientReservationService: TransientReservationService,
    private translateService: TranslateService,
    private eventHttpService: EventHttpService
  ) {
    super();
  }

  get reservations(): Observable<List<Reservation>> {
    return this.currentReservations.asObservable();
  }

  /**
   * Has to be called to retrieve reservations.
   * Is called by resolver.
   *
   * @param eventId
   * @returns RESERVATION_LIST
   */
  loadReservations(eventId: number): Observable<List<Reservation>> {

    return this.reservationHttpService.get(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RESERVATIONS',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<Reservation>) => {
        this.currentReservations.next(reservationList);

        this.reloadEventHasReservationsCache(eventId).pipe(take(1)).subscribe();

        return reservationList;
      });
  }

  /**
   * Has to be called to retrieve reservations.
   * Is called by resolver.
   *
   * @param eventId
   * @param eventPartyId
   * @returns RESERVATION_LIST
   */
  loadReservationsByParty(eventId: number, eventPartyId: number): Observable<List<Reservation>> {

    return this.reservationHttpService.getByParty(eventId, eventPartyId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RESERVATIONS',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<Reservation>) => {
        this.currentReservations.next(reservationList);

        this.reloadEventHasReservationsCache(eventId, eventPartyId).pipe(take(1)).subscribe();

        return reservationList;
      });
  }

  /**
   * Gets the filtered reservation list by an array of status codes and transforms the list into a room stay summary
   *
   * @param statusCode{ReservationStatusCode[]}
   * @returns {Observable<StaySummary>}
   */
  getRoomStaySummaryFilteredByStatus(statusCodes: ReservationStatusCode[]): Observable<RoomStaySummary> {
    return this.getReservationListFilteredByStatus(statusCodes).map(reservationList => {
      return TransactionUtils.transformTransactionsToSummary(reservationList, this.translateService);
    });
  }

  getVendorRoomStaySummaryFilteredByStatus(statusCodes: ReservationStatusCode[], eventPartyId: number): Observable<RoomStaySummary> {
    return this.getVendorReservationListFilteredByStatus(statusCodes, eventPartyId).map(reservationList => {
      return TransactionUtils.transformTransactionsToSummary(reservationList, this.translateService);
    });
  }

  /**
   * Filters reservation list by an array of status codes
   * e.g. checked out
   *
   * @param statusCode{ReservationStatusCode[]}
   * @returns {Observable<List<Reservation>>}
   */
  getReservationListFilteredByStatus(statusCodes: ReservationStatusCode[]): Observable<List<Reservation>> {
    return this.currentReservations.asObservable().map((reservationList: List<Reservation>) => {
      return reservationList.filter((reservation: Reservation) => {
        return statusCodes.find(statusCode => statusCode.toString() === reservation.status) != null;
      }).toList();
    });
  }

  getVendorReservationListFilteredByStatus(statusCodes: ReservationStatusCode[], eventPartyId: number): Observable<List<Reservation>> {
    return this.currentReservations.asObservable().map((reservationList: List<Reservation>) => {
      return reservationList.filter((reservation: Reservation) => {
        return reservation.eventPartyId === eventPartyId
          && statusCodes.find(statusCode => statusCode.toString() === reservation.status) != null;
      }).toList();
    });
  }

  getTotalCommissions(reservationStatusCode: ReservationStatusCode): Observable<number> {
    return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
      return this.getRoomStaySummaryFilteredByStatus([reservationStatusCode]);
    }).map((summary: RoomStaySummary) => {
      return summary.staySummary.totalCommission;
    });
  }

  getVendorTotalCommissions(reservationStatusCode: ReservationStatusCode, eventPartyId: number): Observable<number> {
    return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
      return this.getVendorRoomStaySummaryFilteredByStatus([reservationStatusCode], eventPartyId);
    }).map((summary: RoomStaySummary) => {
      return summary.staySummary.totalCommission;
    });
  }

  getTotalRevenue(reservationStatusCode: ReservationStatusCode): Observable<number> {
    return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
      return this.getRoomStaySummaryFilteredByStatus([reservationStatusCode]);
    }).map((summary: RoomStaySummary) => {
      return summary.staySummary.totalRevenue;
    });
  }

  getVendorTotalRevenue(reservationStatusCode: ReservationStatusCode, eventPartyId: number): Observable<number> {
    return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
      return this.getVendorRoomStaySummaryFilteredByStatus([reservationStatusCode], eventPartyId);
    }).map((summary: RoomStaySummary) => {
      return summary.staySummary.totalRevenue;
    });
  }

  addReservation(reservation: Reservation, source: EventReservationSource, isMultiVendor: boolean): Observable<any> {
    return this.reservationHttpService.add(reservation, source)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_RESERVATION', `Error adding reservation: ${error}`);
        return Observable.throw(error);
      })
      .map((response) => {
        this.flashMessagesService.success(
          'EVENT.RESERVATION.RESERVATION_ADDED',
          null,
          {
            lastName: String (reservation.lastName),
            firstName: !reservation.firstName ? '' : String (`, ${reservation.firstName}`)
          });

        if (isMultiVendor) {
          this.loadReservations(reservation.eventId).subscribe();
        } else {
          this.loadReservationsByParty(reservation.eventId, reservation.eventPartyId).subscribe();
        }

        return response;
      });
  }

  /**
   * Delete a reservation
   *
   * @param reservation
   * @returns {Observable<R>}
   */
  deleteReservation(reservation: Reservation, isMultiVendor: boolean): Observable<any> {
    return this.reservationHttpService.delete(reservation)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.DELETE_RESERVATION', `Error deleting reservation: ${error}`);
        return Observable.throw(error);
      })
      .map(response => {
        this.flashMessagesService.success('EVENT.RESERVATION.RESERVATION_DELETED');
        if (isMultiVendor) {
          this.loadReservations(reservation.eventId).subscribe();
        } else {
          this.loadReservationsByParty(reservation.eventId, reservation.eventPartyId).subscribe();
        }
        return response;
      });
  }

  /**
   * Update a reservation
   *
   * @param reservation
   * @param source
   * @returns {OperatorFunction<T, R>}
   */
  updateReservation(reservation: Reservation, isMultiVendor: boolean): Observable<any> {
    return this.reservationHttpService.update(reservation)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_RESERVATION', `Error updating reservation: ${error}`);
        return Observable.throw(error);
      })
      .map((response) => {
        this.flashMessagesService.success(
          'EVENT.RESERVATION.RESERVATION_UPDATED',
          null,
          {
            lastName: String (reservation.lastName),
            firstName: !reservation.firstName ? '' : String (`, ${reservation.firstName}`)
          });
        if (isMultiVendor) {
          this.loadReservations(reservation.eventId).subscribe();
        } else {
          this.loadReservationsByParty(reservation.eventId, reservation.eventPartyId).subscribe();
        }
        return response;
      });
  }

  /**
   * Import a file of reservations
   *
   * @param eventId
   * @param file
   * @returns {OperatorFunction<T, R>}
   */
  importReservationFile(eventId: number, eventPartyId: number, file: File, isMultiVendor: boolean): Observable<any> {
    return this.reservationHttpService
      .importReservationFile(eventId, eventPartyId, file)
      .map((importResponse) => {
        if (isMultiVendor) {
          this.loadReservations(eventId).subscribe();
        } else {
          this.loadReservationsByParty(eventId, eventPartyId).subscribe();
        }

        return importResponse;
      });
  }

  /**
   * Unlink an out-of-block reservation
   *
   * @param reservation
   * @returns {Observable<R>}
   */
  unlinkReservation(eventReservationId: number, eventId: number, eventPartyId: number, isMultiVendor: boolean): Observable<any> {
    return this.transientReservationService.unlinkTransientReservation(eventReservationId, eventId)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UNLINK_RESERVATION', `Error unlinking reservation: ${error}`);
        return Observable.throw(error);
      })
      .map(response => {
        this.flashMessagesService.success('EVENT.TRANSIENT_RESERVATION.RESERVATION_UNLINKED');
        if (isMultiVendor) {
          this.loadReservations(eventId).subscribe();
        } else {
          this.loadReservationsByParty(eventId, eventPartyId).subscribe();
        }
        return response;
      });
  }

  getReservationsByStatus(eventId: number, statusCodes: ReservationStatusCode[], pageNumber: number, size: number)
  : Observable<Page<Reservation>> {

    return this.reservationHttpService.getReservationsByStatus(eventId, statusCodes, pageNumber, size)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RESERVATIONS',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      });
  }

  getReservationsByStatusByParty(eventId: number, eventPartyId: number, statusCodes: ReservationStatusCode[]
    , pageNumber: number, size: number): Observable<Page<Reservation>> {

    return this.reservationHttpService.getReservationsByStatusByParty(eventId, eventPartyId, statusCodes, pageNumber, size)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_GET_RESERVATIONS',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      });
  }

  getEventHasReservations(): Observable<boolean> {
    return super.getEventHasItems();
  }

  getHasReservationsByEventPartyId(eventPartyId: number): Observable<boolean> {
    return super.getHasItemsByEventPartyId(eventPartyId);
  }

  loadEventHasReservationsCache(eventHasReservationsByVendor: { [key: number]: boolean; }): void {
    super.loadEventHasItemsCache(eventHasReservationsByVendor);
  }

  reloadEventHasReservationsCache(eventId: number, eventPartyId?: number): Observable<EventHasItemsByVendor> {
    let serviceCall: Observable<EventHasItemsByVendor | Response>;
    if (!!eventPartyId) {
      serviceCall =  this.eventHttpService.fetchHasItemsByParty(eventId, eventPartyId);
    } else {
      serviceCall = this.eventHttpService.fetchHasItems(eventId);
    }

    return serviceCall.pipe(
      catchError((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COULD_NOT_CHECK_HAS_RESERVATIONS',
          `Error retrieving items: ${ error }`
        );
        return Observable.throw(error);
      }),
      filter((response: EventHasItemsByVendor | Response) => response instanceof EventHasItemsByVendor),
      tap((eventHasItemsByVendor: EventHasItemsByVendor) => {
        this.loadEventHasReservationsCache(eventHasItemsByVendor.hasReservationsByVendor);
      })
    );
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/reservation-store.ts